import React, { useState, useEffect, useCallback } from "react";
import { Segment, Label, Icon, Statistic, Grid, Responsive, Tab, TabProps, Divider, Button, Embed, Image, Message } from "semantic-ui-react";

import TemplateMain from "../../components/TemplateMain";
import DynamicRepository from "../../Repositories/DynamicRepository";
import { format } from "date-fns";
import ImageZoom from "../../components/ImageZoom";

interface ITotalPessoa {
    Ativos: number;
    Inativos: number;
    Pendentes: number;
    Devendo: number;
}

interface ITotalKits {
    Representante: number;
    Revendedor: number;
}

interface State {
    revendedor: ITotalPessoa;
    kits: ITotalKits;
}

const RepresentanteItems: React.FC = () => {

    const [state, setState] = useState<State>();
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

    const loadTotalGeral = useCallback(async () => {
        setLoading(true);

        const response = await DynamicRepository.DynamicTotals([
            {
                dynamicType: "RV",
                returnCode: "RVATI",
                filter: `Status == 1 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVINA",
                filter: `Status == 0 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVPEN",
                filter: `Status == 2 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVDEV",
                filter: `Devedor == 1`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREP",
                filter: `StatusKit == 2`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREV",
                filter: "StatusKit == 3 && Tipo == 0"
            },
        ]);

        setState({
            revendedor: {
                Ativos: response.RVATI,
                Inativos: response.RVINA,
                Pendentes: response.RVPEN,
                Devendo: response.RVDEV
            },
            kits: {
                Representante: response.KTREP,
                Revendedor: response.KTREV
            }
        });

        setLoading(false);
    }, []);

    const loadTotalMensal = useCallback(async () => {
        setLoading(true);

        const response = await DynamicRepository.DynamicTotals([
            {
                dynamicType: "RV",
                returnCode: "RVATI",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 1 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVINA",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 0 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVPEN",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 2 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVDEV",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Devedor == 1`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREP",
                filter: `StatusKit == 2`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREV",
                filter: `DataEntrega >= "${format(new Date(), "yyyy-MM-01")}" && StatusKit == 3 && Tipo == 0`
            },
        ]);

        setState({
            revendedor: {
                Ativos: response.RVATI,
                Inativos: response.RVINA,
                Pendentes: response.RVPEN,
                Devendo: response.RVDEV
            },
            kits: {
                Representante: response.KTREP,
                Revendedor: response.KTREV
            }
        });

        setLoading(false);
    }, []);

    const handleTabChange = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>, { activeIndex }: TabProps) => {
        setActiveTabIndex(activeIndex as number);
    }, []);

    useEffect(() => {
        if (activeTabIndex === 0) {
            loadTotalMensal();
        }
        else {
            loadTotalGeral();
        }
    }, [activeTabIndex, loadTotalGeral, loadTotalMensal]);

    const renderKit = (
        <>
            <Statistic>
                <Statistic.Value>
                    {state?.kits.Representante || 0}
                </Statistic.Value>
                <Statistic.Label>
                    <Icon name="id badge" color="violet" />
                    {" "}Representante
                </Statistic.Label>
            </Statistic>
            <Statistic>
                <Statistic.Value>
                    {state?.kits.Revendedor || 0}
                </Statistic.Value>
                <Statistic.Label>
                    <Icon name="street view" color="teal" />
                    {" "}Kit's Novos
                </Statistic.Label>
            </Statistic>
        </>
    );

    const date = new Date();
    const actualDay = date.getDate();
    var restam = 20 - actualDay;

    const content = (
        <Grid columns="equal" doubling>
            {
                restam > 0 && restam < 6 ?
                    <Grid.Row>
                        <Grid.Column width="16">
                            <Message icon="warning" header="ATENÇÃO" color="red" content={`falta(m) apenas ${restam} dia(s) para o sistema de conferência ser fechado.`} />
                        </Grid.Column>
                    </Grid.Row>
                    :
                    ""
            }
            <Grid.Row>
                <Grid.Column width="16">
                    <Tab
                        menu={{ secondary: true, pointing: true }}
                        panes={[{ menuItem: "Mensal" }, { menuItem: "Geral" }]}
                        activeIndex={activeTabIndex}
                        onTabChange={handleTabChange}
                    />
                </Grid.Column>
            </Grid.Row>
            
            <Grid.Row>
                <Grid.Column width="16">
                    <Segment color="purple" raised>
                        <Label ribbon content="Kits" size="large" color="purple" />
                        <Responsive as={Statistic.Group} {...Responsive.onlyComputer} widths="2" size="small">
                            {renderKit}
                        </Responsive>
                        <Responsive as={Statistic.Group} {...Responsive.onlyMobile} widths="2" size="small">
                            {renderKit}
                        </Responsive>
                    </Segment>
                </Grid.Column>
            </Grid.Row>

            <Grid.Row>
                <Grid.Column>
                    <Segment color="pink" raised>
                        <Label ribbon content="Revendedores" size="large" color="pink" />
                        <Statistic.Group widths="two" size="small">
                            <Statistic>
                                <Statistic.Value>
                                    {state?.revendedor.Ativos || 0}
                                </Statistic.Value>
                                <Statistic.Label>
                                    <Icon name="check" color="pink" />
                                    {" "}Ativos
                                </Statistic.Label>
                            </Statistic>
                            <Statistic>
                                <Statistic.Value>
                                    {state?.revendedor.Inativos || 0}
                                </Statistic.Value>
                                <Statistic.Label>
                                    <Icon name="times" color="grey" />
                                    {" "}Inativos
                                </Statistic.Label>
                            </Statistic>
                            <Statistic>
                                <Statistic.Value>
                                    {state?.revendedor.Pendentes || 0}
                                </Statistic.Value>
                                <Statistic.Label>
                                    <Icon name="clock" color="orange" />
                                    {" "}Pendentes
                                </Statistic.Label>
                            </Statistic>
                            <Statistic>
                                <Statistic.Value>
                                    {state?.revendedor.Devendo || 0}
                                </Statistic.Value>
                                <Statistic.Label>
                                    <Icon name="user times" color="red" />
                                    {" "}Devendo
                                </Statistic.Label>
                            </Statistic>
                        </Statistic.Group>
                    </Segment>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );

    return (
        <TemplateMain
            loading={loading}
            content={content}
        />
    );
};

export default RepresentanteItems;
